'use client';

import React from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import styled from 'styled-components';
import { useDensity } from '@liftango/ui-tokens';

export interface SegmentOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SegmentedControlProps {
  options: SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  className?: string;
  inline?: boolean;
}

const Group = styled(ToggleGroup.Root)`
  display: flex;
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  overflow: hidden;

  &[data-inline='true'] {
    display: inline-flex;
  }
`;

const Item = styled(ToggleGroup.Item)<{ $height: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: ${(p) => p.$height}px;
  padding: 0 12px;
  border: none;

  &[data-fill='true'] {
    flex: 1 1 auto;
  }
  background: ${(p) => p.theme.colors.neutral.panel};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  cursor: pointer;
  line-height: 1;
  transition:
    background ${(p) => p.theme.transition.color},
    color ${(p) => p.theme.transition.color};

  &:not(:last-child) {
    border-inline-end: 1px solid ${(p) => p.theme.colors.neutral.line};
  }

  &[data-state='on'] {
    background: ${(p) => p.theme.colors.brand.primarySoft};
    color: ${(p) => p.theme.colors.brand.primary};
    font-weight: ${(p) => p.theme.fontWeight.bold};
  }

  &:hover:not([data-state='on']) {
    background: ${(p) => p.theme.colors.neutral.panelAlt};
  }

  &:focus-visible {
    outline: ${(p) => p.theme.focus.width} solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: -2px;
    z-index: ${(p) => p.theme.zIndex.raised};
  }
`;

export function SegmentedControl({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  inline,
}: SegmentedControlProps) {
  const density = useDensity();
  return (
    <Group
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onChange(v);
      }}
      aria-label={ariaLabel}
      className={className}
      data-inline={inline ? 'true' : undefined}
    >
      {options.map((opt) => (
        <Item
          key={opt.value}
          value={opt.value}
          $height={density.controlHeight}
          data-fill={inline ? undefined : 'true'}
        >
          {opt.icon}
          {opt.label}
        </Item>
      ))}
    </Group>
  );
}
